import {
  IconBook,
  IconChevronLeft,
  IconExternalLink,
  IconFolder,
  IconGauge,
  IconHelp,
  IconLock,
  IconLogout,
  IconUsers,
} from '@tabler/icons-react';
import { Avatar, AvatarFallback } from 'design-system/avatar/avatar';
import { Button } from 'design-system/button/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'design-system/tooltip/tooltip';
import { cn } from 'lib/cn';
import {
  DOCS_BASE_URL,
  SUPPORT_PORTAL_URL,
} from 'lib/constants/documentation-urls';
import { Link } from 'react-router';
import { routes, type SettingsRouteKey } from 'routes/routes';
import { type TeamLevelPermissions } from 'types/auth';

import { SidebarFooter } from './sidebar-footer';
import { SidebarLayout } from './sidebar-layout';
import { type SidebarNavIcon, SidebarNavItem } from './sidebar-nav-item';
import { SidebarToggle } from './sidebar-toggle';

interface SettingsNavItem {
  route: SettingsRouteKey;
  label: string;
  icon: SidebarNavIcon;
  permission: TeamLevelPermissions | null;
  /** When true, the item only shows for teams with hard-enforced quota limits. */
  requiresHardLimits?: boolean;
}

const SETTINGS_NAV_ITEMS = [
  {
    route: 'settingsProjects',
    label: 'Projects',
    icon: IconFolder,
    permission: null,
  },
  {
    route: 'settingsTeam',
    label: 'Team',
    icon: IconUsers,
    permission: 'read_team',
  },
  // Usage only shows for teams with hard-enforced quota limits; purely
  // soft-limited (paying) teams see no usage reporting.
  {
    route: 'settingsUsage',
    label: 'Usage',
    icon: IconGauge,
    permission: 'read_team',
    requiresHardLimits: true,
  },
  {
    route: 'settingsAuth',
    label: 'Authentication',
    icon: IconLock,
    permission: 'edit_team',
  },
] as const satisfies readonly SettingsNavItem[];

export const SETTINGS_ACTIVE_ROUTES = [
  'settingsAccount',
  ...SETTINGS_NAV_ITEMS.map((item) => item.route),
] as const;

export type ActiveSettingsItem = (typeof SETTINGS_ACTIVE_ROUTES)[number];

const hoverRevealClass =
  'inline-flex shrink-0 opacity-0 transition-opacity group-hover/nav-link:opacity-100 group-focus-visible/nav-link:opacity-100';

export interface SettingsSidebarProps {
  userName: string;
  /** Account email; its first letter is the avatar fallback. */
  userEmail: string;
  /** The user's team permissions; items whose permission is missing are hidden. */
  permissions: readonly TeamLevelPermissions[];
  /** Undefined until the quota usage loads; gated items stay hidden until then. */
  hasHardLimits?: boolean;
  activeItem: ActiveSettingsItem | null;
  isSigningOut: boolean;
  onBack: () => void;
  onToggle: () => void;
  onSignOut: () => void;
  onAskAi: () => void;
  collapsed?: boolean;
}

export const SettingsSidebar = ({
  userName,
  userEmail,
  permissions,
  hasHardLimits,
  activeItem,
  isSigningOut,
  onBack,
  onToggle,
  onSignOut,
  onAskAi,
  collapsed = false,
}: SettingsSidebarProps) => {
  const avatarInitial = (userEmail.at(0) ?? '').toUpperCase();

  const backButton = (
    <Button
      variant="ghost"
      onClick={onBack}
      aria-label="Back"
      className={cn(
        'justify-start overflow-hidden px-3',
        collapsed && 'w-full gap-0',
      )}
    >
      <IconChevronLeft stroke={1.5} />
      <span
        className={cn(
          'overflow-hidden whitespace-nowrap transition-[max-width,opacity]',
          collapsed ? 'max-w-0 opacity-0' : 'max-w-[160px]',
        )}
      >
        Back
      </span>
    </Button>
  );

  return (
    <SidebarLayout
      collapsed={collapsed}
      slots={{
        header: (
          <div
            className={cn(
              'flex items-center pt-5 pb-4 transition-[padding]',
              collapsed
                ? 'flex-col-reverse gap-1 px-2'
                : 'justify-between px-4',
            )}
          >
            <Tooltip disabled={!collapsed}>
              <TooltipTrigger render={backButton} />
              <TooltipContent side="right">Back</TooltipContent>
            </Tooltip>
            <SidebarToggle collapsed={collapsed} onToggle={onToggle} />
          </div>
        ),
        body: (
          <div className="flex flex-col gap-5">
            <SidebarNavItem
              label={userName}
              tooltip="Account"
              collapsed={collapsed}
              isActive={activeItem === 'settingsAccount'}
              // Recentres the avatar on the icons' centerline; the hidden label's gap would push it off.
              className={collapsed ? 'gap-0 pr-0 pl-2' : undefined}
              leading={
                <Avatar size="sm" shape="circle" className="shrink-0">
                  <AvatarFallback>{avatarInitial}</AvatarFallback>
                </Avatar>
              }
              render={
                <Link
                  to={routes.settingsAccount.build()}
                  aria-label={`Account (${userName})`}
                />
              }
            />
            <div className="flex flex-col gap-1.5">
              {SETTINGS_NAV_ITEMS.filter(
                (item: SettingsNavItem) =>
                  (!item.permission || permissions.includes(item.permission)) &&
                  (!item.requiresHardLimits || hasHardLimits === true),
              ).map((item) => (
                <SidebarNavItem
                  key={item.route}
                  label={item.label}
                  collapsed={collapsed}
                  isActive={activeItem === item.route}
                  icon={item.icon}
                  render={
                    <Link
                      to={routes[item.route].build()}
                      aria-label={item.label}
                    />
                  }
                />
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              <SidebarNavItem
                label="Documentation"
                collapsed={collapsed}
                icon={IconBook}
                trailing={
                  <span className={cn(hoverRevealClass, collapsed && 'hidden')}>
                    <IconExternalLink size={16} stroke={1.5} />
                  </span>
                }
                render={
                  <a
                    href={DOCS_BASE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Documentation"
                  />
                }
              />
              <SidebarNavItem
                label="Support"
                collapsed={collapsed}
                icon={IconHelp}
                trailing={
                  <span className={cn(hoverRevealClass, collapsed && 'hidden')}>
                    <IconExternalLink size={16} stroke={1.5} />
                  </span>
                }
                render={
                  <a
                    href={SUPPORT_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Support"
                  />
                }
              />
            </div>
            <SidebarNavItem
              label="Sign out"
              collapsed={collapsed}
              icon={IconLogout}
              className="disabled:text-foreground-tertiary disabled:pointer-events-none"
              render={
                <button
                  type="button"
                  onClick={onSignOut}
                  disabled={isSigningOut}
                  aria-label="Sign out"
                />
              }
            />
          </div>
        ),
        footer: (
          <SidebarFooter
            onAskAi={onAskAi}
            showSettings={false}
            collapsed={collapsed}
          />
        ),
      }}
    />
  );
};
SettingsSidebar.displayName = 'SettingsSidebar';
